/**
 * Authentication Flow Hook
 *
 * Manages the complete user authentication process including provider initialization,
 * user context loading, and error handling. Supports both MSAL (Azure AD B2C) and
 * Kinde authentication providers via the unified useAuth hook.
 *
 * Extracted from UserRequiredContainer to improve testability and reusability.
 *
 * @module useAuthenticationFlow
 */

import { useCallback } from 'react'

import { useCreateUserMutation, useGetOrCreateUserContextMutation } from '@/app/services/user'
import { LOADING_STEPS } from '@/common/utils'
import { createErrorFromRtkError, UserContextError, UserNotFoundError } from '@/common/utils/AuthErrors'

import { useAuth } from './useAuth'
import useLoadingContext from './useLoadingContext'

/**
 * Authentication flow hook for managing user authentication and context loading.
 *
 * Handles the complete authentication sequence:
 * 1. Auth provider initialization (MSAL or Kinde)
 * 2. User context retrieval and validation
 * 3. User creation for 404 responses (new users)
 * 4. Comprehensive error handling with typed errors
 *
 * @returns {Object} Authentication flow functions and state
 * @returns {Object.initializeAuthentication} Complete auth initialization
 * @returns {Object.loadUserContext} Load/create user context
 * @returns {Object.createUserIfNeeded} Handle 404 user creation
 * @returns {Object.validateUserContext} Validate user context data
 */
export const useAuthenticationFlow = () => {
    const { ensureInitialized, getActiveAccount, provider, user: userDto } = useAuth()
    const [createUser] = useCreateUserMutation()
    const [getOrCreateUserContext] = useGetOrCreateUserContextMutation()

    const { setStepLoading, setStepSuccess } = useLoadingContext()

    /**
     * Creates a new user when one doesn't exist (handles 404 responses).
     *
     * @param {Object} userDtoParam - Unified user DTO (from useAuth)
     * @returns {Promise<Object>} Created user object
     * @throws {UserNotFoundError} If user creation fails
     */
    const createUserIfNeeded = useCallback(
        async (userDtoParam) => {
            try {
                console.debug(`[Auth Flow] Creating new user from ${provider} account`)
                const createdUser = await createUser({ user: userDtoParam }).unwrap()
                console.debug('[Auth Flow] Successfully created new user:', createdUser.user?.userId)
                return createdUser
            } catch (error) {
                console.error('[Auth Flow] Failed to create user:', error)
                throw new UserNotFoundError(error)
            }
        },
        [createUser, provider]
    )

    /**
     * Loads user context, creating user if needed for 404 responses.
     *
     * @param {Object} userDtoParam - Unified user DTO (from useAuth)
     * @returns {Promise<Object>} User context with user and organization info
     * @throws {UserContextError} If context loading fails
     * @throws {UserNotFoundError} If user creation fails
     */
    const loadUserContext = useCallback(
        async (userDtoParam) => {
            console.debug('[Auth Flow] Loading user context from API')

            try {
                // Try to get existing user context
                const userContext = await getOrCreateUserContext({ user: userDtoParam }).unwrap()
                console.debug('[Auth Flow] Successfully loaded user context for:', userContext.user?.userId)
                return userContext
            } catch (contextError) {
                // Handle 404 - user doesn't exist, create new user
                if (contextError?.status === 404) {
                    console.debug('[Auth Flow] User not found (404), creating new user')
                    // createUserIfNeeded can throw UserNotFoundError - let it bubble up
                    return await createUserIfNeeded(userDtoParam)
                }

                // For other errors, create appropriate error and throw it directly
                console.error('[Auth Flow] Failed to load user context:', contextError)
                throw createErrorFromRtkError(contextError, 'userContext')
            }
        },
        [createUserIfNeeded, getOrCreateUserContext]
    )

    /**
     * Validates that user context contains required data.
     *
     * @param {Object} userContext - User context from API
     * @throws {UserContextError} If user data validation fails
     */
    const validateUserContext = useCallback((userContext) => {
        if (!userContext?.user) {
            console.error('[Auth Flow] User context validation failed: missing user object')
            throw new UserContextError('User context is missing user information', null)
        }

        console.debug('[Auth Flow] User context validation passed for user:', userContext.user.userId)
    }, [])

    /**
     * Complete authentication initialization sequence.
     *
     * Orchestrates:
     * 1. Auth provider initialization (MSAL or Kinde) and account retrieval
     * 2. User context loading (with automatic user creation for 404s)
     * 3. Context validation
     *
     * @returns {Promise<Object>} Complete authentication result with account, userContext, and userDto
     * @throws {MsalError} If MSAL initialization fails
     * @throws {UserContextError} If user context operations fail
     * @throws {UserNotFoundError} If user creation fails
     * @throws {NoOrganisationError} If user has no selected organization
     */
    const initializeAuthentication = useCallback(async () => {
        try {
            console.debug(`[Auth Flow] Starting authentication initialization (${provider})`)

            // Step 1: Initialize auth provider and get active account
            setStepLoading(LOADING_STEPS.AUTHENTICATION)

            // ensureInitialized is a no-op for Kinde (auto-initializes via KindeProvider)
            await ensureInitialized()
            const account = await getActiveAccount()

            if (!account) {
                throw new UserContextError('No active account found', null)
            }

            // Verify unified user DTO is available from useAuth
            if (!userDto) {
                throw new UserContextError('User DTO not available', null)
            }

            setStepSuccess(LOADING_STEPS.AUTHENTICATION)

            console.debug(`[Auth Flow] ${provider} account retrieved, loading user context`)

            // Step 2: Load user context (with automatic user creation if needed)
            setStepLoading(LOADING_STEPS.USER_CONTEXT)

            // Pass unified DTO to loadUserContext (already normalized by useAuth)
            const userContext = await loadUserContext(userDto)

            // Step 3: Validate user context
            validateUserContext(userContext)

            console.debug('[Auth Flow] Authentication initialization completed successfully')

            setStepSuccess(LOADING_STEPS.USER_CONTEXT)

            return {
                account, // Raw provider account (for compatibility)
                userContext,
                userDto, // Unified DTO for convenience
                provider, // Auth provider used
            }
        } catch (error) {
            console.error('[Auth Flow] Authentication initialization failed:', error)
            throw error // Re-throw to maintain error type for useUserInitialization to handle
        }
    }, [
        ensureInitialized,
        getActiveAccount,
        loadUserContext,
        provider,
        setStepLoading,
        setStepSuccess,
        userDto,
        validateUserContext,
    ])

    return {
        initializeAuthentication,
        loadUserContext,
    }
}
